import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  MenuItem,
  FormControlLabel,
  Switch,
  Paper,
  IconButton,
  Tooltip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Grid } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { matchPlayerApi, matchApi, playerApi } from '../services/api';
import MatchAutocomplete from '../components/MatchAutocomplete';
import PlayerAutocomplete from '../components/PlayerAutocomplete';
import { MatchPlayer, Match, Player, PaginatedResponse } from '../models';

interface FilterState {
  match: Match | null;
  player: Player | null;
}

interface MatchPlayerFormValues {
  id: number;
  player: Player;
  match: number | null;
  team: any;
  role: string;
  lineup_status: string;
  credits: number;
  selected_perc: number;
  captain_perc: number;
  vice_captain_perc: number;
  series_points: number;
  series_avg_points: number;
  total_points: number;
}

const validationSchema = yup.object({
  player: yup.object().shape({
    id: yup.number().required('Player is required'),
  }).required('Player is required'),
  match: yup.object().shape({
    id: yup.number().required('Match is required'),
  }).required('Match is required'),
  team: yup.object().shape({
    id: yup.number().required('Team is required'),
  }).nullable(),
  role: yup.string().required('Role is required'),
  lineup_status: yup.string().required('Lineup status is required'),
  credits: yup.number(),
  selected_perc: yup.number(),
  captain_perc: yup.number(),
  vice_captain_perc: yup.number(),
  series_points: yup.number(),
  series_avg_points: yup.number(),
  total_points: yup.number(),
});

const roleOptions = [
  { value: 'BAT', label: 'BAT' },
  { value: 'BOWL', label: 'BOWL' },
  { value: 'ALL', label: 'ALL' },
  { value: 'WK', label: 'WK' },
];

const lineupStatusOptions = [
  { value: 'PLAYING', label: 'Playing' },
  { value: 'NOT_PLAYING', label: 'Not Playing' },
  { value: 'LAST_PLAYED', label: 'Last Played' },
  { value: 'UNAVAILABLE', label: 'Unavailable' },
  { value: 'X_FACTOR_SUBSTITUTE', label: 'X-Factor Substitute' },
  { value: 'USED_X_FACTOR_SUBSTITUTE', label: 'Used X-Factor Substitute' },
];

const MatchPlayers: React.FC = () => {
  const [matchPlayers, setMatchPlayers] = useState<MatchPlayer[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [selectedMatchPlayer, setSelectedMatchPlayer] = useState<MatchPlayer | null>(null);
  const [page, setPage] = useState(1);
  const pageSize = 50;
  const [filters, setFilters] = useState<FilterState>({
    match: null,
    player: null,
  });
  const [availablePlayers, setAvailablePlayers] = useState<Player[]>([]);

  useEffect(() => {
    const fetchPlayers = async () => {
      try {
        const response = await playerApi.getAll();
        if (response.data && response.data.results) {
          setAvailablePlayers(response.data.results);
        }
      } catch (error) {
        console.error('Error fetching players:', error);
      }
    };
    fetchPlayers();
  }, []);

  // Check URL parameters when component mounts
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const matchId = urlParams.get('match');
    const playerId = urlParams.get('player');
    
    const fetchInitialFilters = async () => {
      try {
        setLoading(true);
        const filtersToUpdate = { ...filters };
        
        if (matchId) {
          try {
            const response = await matchApi.getById(Number(matchId));
            if (response.data) {
              filtersToUpdate.match = response.data;
            }
          } catch (error) {
            console.error('Error fetching match details:', error);
          }
        }
        
        if (playerId) {
          try {
            const response = await playerApi.getById(Number(playerId));
            if (response.data) {
              filtersToUpdate.player = response.data;
            }
          } catch (error) {
            console.error('Error fetching player details:', error);
          }
        }
        
        setFilters(filtersToUpdate);
      } catch (error) {
        console.error('Error fetching initial filters:', error);
      } finally {
        setLoading(false);
      }
    };
    
    if (matchId || playerId) {
      fetchInitialFilters();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns: GridColDef[] = [
    // { 
    //   field: 'id', 
    //   headerName: 'ID', 
    //   width: 120, 
    //   sortable: true,
    //   filterable: true,
    //   type: 'string'
    // },
    { 
      field: 'prev_bat_pos', 
      headerName: 'BP', 
      width: 50, 
      sortable: true,
      filterable: false,
      type: 'number'
    },
    { 
      field: 'player',
      headerName: 'Player',
      width: 200,
      sortable: true,
      valueGetter: (params: any) => params?.name || '',
    },
    {
      field: 'd_id',
      headerName: 'D ID',
      width: 80,
    },
    {
      field: 'mt_id',
      headerName: 'MT ID',
      width: 80,
    },
    {
      field: 'c_id',
      headerName: 'C ID',
      width: 80,
    },
    { 
      field: 'match',
      headerName: 'Match',
      width: 150,
      sortable: true,
      valueGetter: (params: any) => params?.name || '',
    },
    { 
      field: 'team_name',
      headerName: 'Team',
      width: 100,
      sortable: true,
    },
    { 
      field: 'role', 
      headerName: 'Role', 
      width: 100, 
      sortable: true,
      filterable: true,
      type: 'singleSelect',
      valueOptions: roleOptions.map(opt => opt.value),
      renderCell: (params: any) => {
        const role = roleOptions.find(r => r.value === params.value);
        return role ? role.label : params.value;
      }
    },
    {
      field: 'lineup_status',
      headerName: 'Status',
      width: 100,
      sortable: true,
      filterable: true,
      type: 'singleSelect',
      valueOptions: lineupStatusOptions.map(opt => opt.value),
      renderCell: (params) => {
        const status = lineupStatusOptions.find(s => s.value === params.value);
        const statusLabel = status ? status.label : params.value;
        
        return (
          <Box
            sx={{
              backgroundColor:
                params.value === 'PLAYING'
                  ? '#4caf50'
                  : params.value === 'NOT_PLAYING'
                  ? '#f44336'
                  : params.value === 'UNAVAILABLE'
                  ? '#9e9e9e'
                  : params.value === 'LAST_PLAYED'
                  ? '#ff9800'
                  : '#2196f3',
              color: 'white',
              padding: '0px 6px',
              fontSize: '0.75rem',
              borderRadius: '10px',
              textTransform: 'capitalize',
              display: 'inline-block',
              minWidth: '60px',
              textAlign: 'center',
            }}
          >
            {statusLabel}
          </Box>
        );
      },
    },
    { 
      field: 'credits', 
      headerName: 'C', 
      width: 50, 
      sortable: true,
      type: 'number',
    },
    { 
      field: 'selected_perc', 
      headerName: 'S%', 
      width: 70, 
      sortable: true,
      type: 'number',
      // valueFormatter: (params) => {
      //   if (params != null) {
      //     return `${params}%`;
      //   }
      //   return '';
      // },
    },
    { 
      field: 'captain_perc', 
      headerName: 'C%', 
      width: 70, 
      sortable: true,
      type: 'number',
      // valueFormatter: (params) => {
      //   if (params != null) {
      //     return `${params}%`;
      //   }
      //   return '';
      // },
    },
    { 
      field: 'vice_captain_perc', 
      headerName: 'VC%', 
      width: 70,
      sortable: true,
      type: 'number',
      // valueFormatter: (params) => {
      //   if (params != null) {
      //     return `${params}%`;
      //   }
      //   return '';
      // },
    },
    { 
      field: 'series_points', 
      headerName: 'Pts', 
      width: 70, 
      sortable: true,
      type: 'number',
    },
    { 
      field: 'series_avg_points', 
      headerName: 'A Pt', 
      width: 70, 
      sortable: true,
      type: 'number',
    },
    { 
      field: 'in_dream_team', 
      headerName: 'DT', 
      width: 70, 
      sortable: true,
      type: 'boolean',
      renderCell: (params) => params.value ? 'Yes' : 'No'
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      sortable: false,
      filterable: false,
      renderCell: (params: any) => (
        <Box>
          <Tooltip title="Edit">
            <IconButton
              color="primary"
              size="small"
              onClick={() => handleEdit(params.row)}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              color="error"
              size="small"
              onClick={() => handleDelete(params.row.id)}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Refresh Player Stats">
            <IconButton
              color="info"
              size="small"
              onClick={() => handleRefreshPlayerStats(params.row.player.id)}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const formik = useFormik<MatchPlayerFormValues>({
    initialValues: {
      id: 0,
      player: {} as Player,
      match: null,
      team: null,
      role: 'BAT',
      lineup_status: 'NOT_PLAYING',
      credits: 0,
      selected_perc: 0,
      captain_perc: 0,
      vice_captain_perc: 0,
      series_points: 0,
      series_avg_points: 0,
      total_points: 0,
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        if (selectedMatchPlayer) {
          await matchPlayerApi.update(selectedMatchPlayer.id, values);
        } else {
          await matchPlayerApi.create(values);
        }
        fetchMatchPlayers(page, pageSize);
        handleClose();
      } catch (error) {
        console.error('Error saving match player:', error);
      }
    },
  });

  const fetchMatchPlayers = React.useCallback(async (pageNumber: number = 1, size: number = pageSize) => {
    try {
      setLoading(true);
      const params: any = {
        page: pageNumber,
        page_size: size,
      };

      if (filters.match) {
        params.match = filters.match.id;
      }
      
      if (filters.player) {
        params.player = filters.player.id;
      }

      const response = await matchPlayerApi.getAll(params);
      const data = response.data as PaginatedResponse<MatchPlayer>;
      setMatchPlayers(data.results);
      // setTotalCount(data.count);
    } catch (error) {
      console.error('Error fetching match players:', error);
    } finally {
      setLoading(false);
    }
  }, [filters, pageSize]);

  useEffect(() => {
    fetchMatchPlayers(page, pageSize);
  }, [page, pageSize, filters, fetchMatchPlayers]);

  const handleFilterChange = (name: keyof FilterState, value: any) => {
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
    setPage(1); // Reset to first page when filters change
    
    // Update URL with query parameters for deep linking
    const searchParams = new URLSearchParams(window.location.search);
    
    if (name === 'match' && value) {
      searchParams.set('match', value.id.toString());
    } else if (name === 'match' && !value) {
      searchParams.delete('match');
    }
    
    if (name === 'player' && value) {
      searchParams.set('player', value.id.toString());
    } else if (name === 'player' && !value) {
      searchParams.delete('player');
    }
    
    const newUrl = window.location.pathname + (searchParams.toString() ? `?${searchParams.toString()}` : '');
    window.history.pushState({}, '', newUrl);
  };

  const handleEdit = (matchPlayer: MatchPlayer) => {
    setSelectedMatchPlayer(matchPlayer);
    formik.setValues({
      id: matchPlayer.id,
      player: matchPlayer.player,
      match: matchPlayer.match || null,
      team: matchPlayer.team ? { id: matchPlayer.team } : null,
      role: matchPlayer.role || 'BAT',
      lineup_status: matchPlayer.lineup_status || 'NOT_PLAYING',
      credits: matchPlayer.credits || 0,
      selected_perc: matchPlayer.selected_perc || 0,
      captain_perc: matchPlayer.captain_perc || 0,
      vice_captain_perc: matchPlayer.vice_captain_perc || 0,
      series_points: matchPlayer.series_points || 0,
      series_avg_points: matchPlayer.series_avg_points || 0,
      total_points: matchPlayer.total_points || 0,
    });
    setOpen(true);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this match player?')) {
      try {
        await matchPlayerApi.delete(id);
        fetchMatchPlayers(page, pageSize);
      } catch (error) {
        console.error('Error deleting match player:', error);
      }
    }
  };

  const handleRefreshPlayerStats = async (playerId: number) => {
    try {
      await playerApi.refreshStats(playerId);
      // Show success message
      alert('Player stats refresh initiated. This may take a few moments.');
    } catch (error) {
      console.error('Error refreshing player stats:', error);
      alert('Failed to refresh player stats. Please try again later.');
    }
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedMatchPlayer(null);
    formik.resetForm();
  };

  return (
    <Box sx={{ height: "calc(100vh - 240px)", width: "100%" }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h4">Match Players</Typography>
        <Button variant="contained" color="primary" onClick={() => setOpen(true)}>
          Add Match Player
        </Button>
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <MatchAutocomplete
              value={filters.match}
              onChange={(value) => handleFilterChange('match', value)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <PlayerAutocomplete
              value={filters.player}
              onChange={(value) => handleFilterChange('player', value)}
              matchId={filters.match?.id || null}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Button 
              variant="outlined" 
              color="primary" 
              onClick={() => {
                setFilters({ match: null, player: null });
                // Update URL to remove query parameters
                const newUrl = window.location.pathname;
                window.history.pushState({}, '', newUrl);
              }}
            >
              Clear Filters
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <DataGrid
        rows={matchPlayers}
        columns={columns}
        initialState={{
          pagination: {
            paginationModel: { pageSize: 50, page: 0 },
          },
          columns: {
            columnVisibilityModel: {
              match: false,
            },
          },
        }}
        pageSizeOptions={[10, 25, 50, 100]}
        loading={loading}
        density="compact"
      />

      {/* <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
        <Pagination
          count={Math.ceil(totalCount / pageSize)}
          page={page}
          onChange={handlePageChange}
          color="primary"
          showFirstButton
          showLastButton
        />
      </Box> */}

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <form onSubmit={formik.handleSubmit}>
          <DialogTitle>
            {selectedMatchPlayer ? 'Edit Match Player' : 'Add Match Player'}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  name="player.id"
                  label="Player"
                  value={formik.values.player.id || ''}
                  onChange={(e) => {
                    const selectedPlayer = availablePlayers.find(p => p.id === Number(e.target.value));
                    formik.setFieldValue('player', selectedPlayer || {});
                  }}
                  error={formik.touched.player?.id && Boolean(formik.errors.player?.id)}
                  helperText={formik.touched.player?.id && formik.errors.player?.id}
                >
                  {availablePlayers.map((player) => (
                    <MenuItem key={player.id} value={player.id}>
                      {player.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <MatchAutocomplete
                  value={formik.values.match ? { id: formik.values.match } as unknown as Match : null}
                  onChange={(match) => {
                    formik.setFieldValue('match', match ? match.id : null);
                  }}
                  error={formik.touched.match && Boolean(formik.errors.match)}
                  helperText={formik.touched.match && typeof formik.errors.match === 'string' ? formik.errors.match : ''}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  name="role"
                  label="Role"
                  value={formik.values.role}
                  onChange={formik.handleChange}
                  error={formik.touched.role && Boolean(formik.errors.role)}
                  helperText={formik.touched.role && formik.errors.role}
                >
                  {roleOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  name="lineup_status"
                  label="Lineup Status"
                  value={formik.values.lineup_status}
                  onChange={formik.handleChange}
                  error={formik.touched.lineup_status && Boolean(formik.errors.lineup_status)}
                  helperText={formik.touched.lineup_status && formik.errors.lineup_status}
                >
                  {lineupStatusOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  name="credits"
                  label="Credits"
                  type="number"
                  value={formik.values.credits}
                  onChange={formik.handleChange}
                  error={formik.touched.credits && Boolean(formik.errors.credits)}
                  helperText={formik.touched.credits && formik.errors.credits}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  name="selected_perc"
                  label="Selected %"
                  type="number"
                  value={formik.values.selected_perc}
                  onChange={formik.handleChange}
                  error={formik.touched.selected_perc && Boolean(formik.errors.selected_perc)}
                  helperText={formik.touched.selected_perc && formik.errors.selected_perc}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  name="captain_perc"
                  label="Captain %"
                  type="number"
                  value={formik.values.captain_perc}
                  onChange={formik.handleChange}
                  error={formik.touched.captain_perc && Boolean(formik.errors.captain_perc)}
                  helperText={formik.touched.captain_perc && formik.errors.captain_perc}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  name="vice_captain_perc"
                  label="Vice Captain %"
                  type="number"
                  value={formik.values.vice_captain_perc}
                  onChange={formik.handleChange}
                  error={formik.touched.vice_captain_perc && Boolean(formik.errors.vice_captain_perc)}
                  helperText={formik.touched.vice_captain_perc && formik.errors.vice_captain_perc}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formik.values.lineup_status === 'PLAYING'}
                      onChange={(e) => {
                        formik.setFieldValue(
                          'lineup_status',
                          e.target.checked ? 'PLAYING' : 'NOT_PLAYING'
                        );
                      }}
                    />
                  }
                  label="Is Playing"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Button type="submit" variant="contained" color="primary">
              {selectedMatchPlayer ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default MatchPlayers;
